import { Component, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CreateParkingTariffDTO,
  Currency,
  ParkingTariffDTO,
  ParkingTariffLimitedDTO,
  PlaceOfResidence,
  UpdateParkingTariffDTO,
  ParkingTariffTieredPriceDTO,
  TariffPriceType,
  BlacklistLicensePlateDTO
} from 'src/app/base/models/models.swagger.generated';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { ToastService } from 'src/app/base/services/toast.service';
import { merge, Observable, of } from 'rxjs';
import { mapTo, startWith } from 'rxjs/operators';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { TabComponent, TabModule } from '@syncfusion/ej2-angular-navigations';
import { TariffDetailsPriceValidators } from './tariff-details-prices/tariff-details-prices.validators';
import { FormHelper } from 'src/app/base/shared/form-helper';
import { DateHelper } from 'src/app/base/shared/date-helper';
import { BitHelper } from 'src/app/base/shared/bit-helper';
import { MultiFieldDurationPickerHelper } from 'src/app/base/shared/multi-field-duration-picker/multi-field-duration-picker-helper';
import { WeekDayFlagsHelper } from 'src/app/base/shared/utils/week-day-flags.helper';
import { TariffDetailsValidators } from './tariff-details.validators';
import { TariffDetailsService } from './tariff-details.service';
import { AdministrationItemType, DetailsInitInfo } from '../../data-structures';
import { DaysHoursMinutesFormType, TariffFormType, TariffGeneralFormType, TariffPricesFormType, TimeWindowsFormType, WeekdaysFormType } from './tariff-details-from-types';
import { TranslateModule } from '@ngx-translate/core';
import { TariffDetailsZonesComponent } from './tariff-details-zones/tariff-details-zones.component';
import { TariffDetailsPricesComponent } from './tariff-details-prices/tariff-details-prices.component';
import { TariffDetailsGeneralComponent } from './tariff-details-general/tariff-details-general.component';
import { NgIf } from '@angular/common';
import { TariffDetailsBlacklistComponent } from './tariff-details-blacklist/tariff-details-blacklist.component';

@Component({
    selector: 'app-tariff-details',
    templateUrl: './tariff-details.component.html',
    styleUrls: ['./tariff-details.component.scss'],
    imports: [NgIf, TabModule, TariffDetailsGeneralComponent, TariffDetailsPricesComponent, TariffDetailsZonesComponent, TranslateModule, TariffDetailsBlacklistComponent]
})
export class TariffDetailsComponent {
  @Input() public set detailsInitInfo(value: DetailsInitInfo) {
    const valueStr = value ? JSON.stringify(value) : null;
    const detailsInitInfoStr = this._detailInitInfo ? JSON.stringify(this._detailInitInfo) : null;
    if (valueStr != detailsInitInfoStr) {
      this.tariffService.reset();
      this._detailInitInfo = value;
      if (value?.id) {
        this.isAddTariff = false;
        this.getTariff(value.id);
      } else {
        this.selectedTariff = null;
        this.isAddTariff = true;
        this.resetForm();
      }
    }
  }

  @Output() public cancel = new EventEmitter<void>();
  @Output() public tariffCreated = new EventEmitter<ParkingTariffDTO>();
  @Output() public tariffUpdated = new EventEmitter<ParkingTariffDTO>();
  @Output() public tariffDeleted = new EventEmitter<number>();
  @Output() public hasPendingDetailChangesChanged = new EventEmitter<boolean>();

  @ViewChild('tabGroup') public tabGroup: TabComponent;
  public tariffs: ParkingTariffLimitedDTO[];
  public selectedTariff: ParkingTariffDTO;
  public tariffForm: FormGroup<TariffFormType>;
  public formChanged = false;
  public isAddTariff = false;
  public saveButtonDisabled = false;

  public get isBusy(): boolean {
    return this.tariffService.isBusy;
  }

  public get isReadonly(): boolean {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public animation: object = {
    previous: { effect: '', duration: 0, easing: '' },
    next: { effect: '', duration: 0, easing: '' },
  };

  private _detailInitInfo: DetailsInitInfo;
  private formInitialValue;
  private isBlacklistChanged: boolean = false;

  constructor(private tariffService: TariffDetailsService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private modalService: ModalService) {
    this.tariffForm = this.fb.group<{ general: FormGroup<TariffGeneralFormType>, prices: FormGroup<TariffPricesFormType>, blacklist: FormControl<BlacklistLicensePlateDTO[]> }>({
      // create nested formgroup to pass to child
      general: this.fb.group<TariffGeneralFormType>({
        id: this.fb.control<number>(null),
        name: this.fb.control<string>(null, Validators.required),
        active: this.fb.control<boolean>(true, Validators.required),
        description: this.fb.control<string>(null),
        modifiedBy: this.fb.control<string>(null),
        modifiedOn: this.fb.control<string>(null),
        placeOfResidence: this.fb.control<PlaceOfResidence>(PlaceOfResidence.LocalCommunity),
        isTwentyFourSevenTariff: this.fb.control<boolean>(false),
        isDeviceControlTariff: this.fb.control<boolean>(false),
        timeWindows: this.fb.group<TimeWindowsFormType>({
          weekdays: this.fb.group<WeekdaysFormType>({
            allowedMonday: this.fb.control<boolean>(false),
            allowedTuesday: this.fb.control<boolean>(false),
            allowedWednesday: this.fb.control<boolean>(false),
            allowedThursday: this.fb.control<boolean>(false),
            allowedFriday: this.fb.control<boolean>(false),
            allowedSaturday: this.fb.control<boolean>(false),
            allowedSunday: this.fb.control<boolean>(false),
          }, { validators: [CustomValidatorsFactory.atLeastOneFromFormGroupRequired()] }),
          startTime: this.fb.control<Date>(null, Validators.required),
          durationForm: this.fb.group<DaysHoursMinutesFormType>({
            days: this.fb.control<number>(null, Validators.min(0)),
            hours: this.fb.control<number>(null, Validators.min(0)),
            minutes: this.fb.control<number>(null, Validators.min(0))
          }, { validators: [CustomValidatorsFactory.atLeastOneFromFormGroupRequired()] })
        }, { validators: [TariffDetailsValidators.noOverlappingTimeWindows] }),
        maxParkingDurationInMinForm: this.fb.group<DaysHoursMinutesFormType>({
          days: this.fb.control<number>(null, Validators.min(0)),
          hours: this.fb.control<number>(null, Validators.min(0)),
          minutes: this.fb.control<number>(null, Validators.min(0)),
        }),
      }),
      prices: this.fb.group<TariffPricesFormType>({
        tariffCurrency: this.fb.control<Currency>(Currency.CHF),
        priceType: this.fb.control<TariffPriceType>(TariffPriceType.LinearPrice),
        pricePerHour: this.fb.control<number>(null, [Validators.required, Validators.min(0)]),
        maximumPrice: this.fb.control<number>(null, [Validators.min(0)]),
        fixedPrice: this.fb.control<number>(null, [Validators.required, Validators.min(0)]),
        tieredPrices: this.fb.control<ParkingTariffTieredPriceDTO[]>([]),
        linearPriceMinStepInMinutes: this.fb.control<number>(1, Validators.min(1))
      }),
      blacklist: this.fb.control<BlacklistLicensePlateDTO[]>([])
    });
    this.formInitialValue = JSON.parse(JSON.stringify(this.tariffForm.value));
    this.tariffForm.statusChanges
      .pipe(
        startWith(this.tariffForm.status),
      )
      .subscribe(status => {
        const hasChanges = !(JSON.stringify(this.formInitialValue) === JSON.stringify(this.tariffForm.value));
        this.updateFormChangedState(hasChanges);
        this.saveButtonDisabled = ((status !== 'VALID') || !hasChanges) && !this.isBlacklistChanged;
      });

    this.tariffForm.controls.prices.controls.priceType.valueChanges.subscribe(value => {
      const priceFormGroup = this.tariffForm.controls.prices;
      if (value === TariffPriceType.LinearPrice) {
        priceFormGroup.controls.pricePerHour.setValidators([Validators.required, Validators.min(0)]);
        priceFormGroup.controls.fixedPrice.clearValidators();
        priceFormGroup.controls.tieredPrices.clearValidators();
      } else if (value === TariffPriceType.FixedPrice) {
        priceFormGroup.controls.pricePerHour.clearValidators();
        priceFormGroup.controls.fixedPrice.setValidators([Validators.required, Validators.min(0)]);
        priceFormGroup.controls.tieredPrices.clearValidators();
      } else {
        priceFormGroup.controls.pricePerHour.clearValidators();
        priceFormGroup.controls.fixedPrice.clearValidators();
        priceFormGroup.controls.tieredPrices.setValidators([TariffDetailsPriceValidators.assertMaxParkingTimeCoverageOfTieredPrices]);
      }
      priceFormGroup.controls.pricePerHour.updateValueAndValidity();
      priceFormGroup.controls.fixedPrice.updateValueAndValidity();
      priceFormGroup.controls.tieredPrices.updateValueAndValidity();
    });
    this.tariffForm.controls.general.controls.maxParkingDurationInMinForm.controls.days.valueChanges.subscribe(value => {
      this.tariffForm.controls.prices.controls.tieredPrices.updateValueAndValidity();
    });
    this.tariffForm.controls.general.controls.maxParkingDurationInMinForm.controls.hours.valueChanges.subscribe(value => {
      this.tariffForm.controls.prices.controls.tieredPrices.updateValueAndValidity();
    });
    this.tariffForm.controls.general.controls.maxParkingDurationInMinForm.controls.minutes.valueChanges.subscribe(value => {
      this.tariffForm.controls.prices.controls.tieredPrices.updateValueAndValidity();
    });
    this.tariffForm.controls.general.controls.isTwentyFourSevenTariff.valueChanges.subscribe(value => {
      if (value) {
        this.tariffForm.controls.general.controls.timeWindows.controls.startTime.disable();;
        this.tariffForm.controls.general.controls.timeWindows.controls.durationForm.disable();
      } else {
        this.tariffForm.controls.general.controls.timeWindows.controls.startTime.enable();
        this.tariffForm.controls.general.controls.timeWindows.controls.durationForm.enable();
      }
    });

    this.tariffForm.controls.general.controls.isDeviceControlTariff.valueChanges.subscribe(value => {
      if (value) {
        this.tariffForm.controls.prices.controls.priceType.setValue(TariffPriceType.FixedPrice);
        this.tariffForm.controls.prices.controls.priceType.disable();
      } else {
        this.tariffForm.controls.prices.controls.priceType.enable();
      }
    });
    this.tariffForm.controls.blacklist.valueChanges.subscribe(value => {
      this.updateFormChangedState(true);
    });

    FormHelper.updateTreeValidity(this.tariffForm, !!this.tariffForm.controls.general.controls.name.value);
  }

  public get cancelButtonText(): string {
    if (this.selectedTariff) {
      return 'i18n.shared.cancel';
    }
    return 'i18n.shared.close';
  }

  public get maxParkingDuration(): number {
    const maxParkingDurationControl = this.tariffForm.controls.general.controls.maxParkingDurationInMinForm;
    const days = maxParkingDurationControl.controls.days.value ?? 0;
    const hours = maxParkingDurationControl.controls.hours.value ?? 0;
    const minutes = maxParkingDurationControl.controls.minutes.value ?? 0;
    return (days * 24 + hours) * 60 + minutes;
  }

  @HostListener('window:beforeunload')
  beforeUnload() {
    return JSON.stringify(this.formInitialValue) === JSON.stringify(this.tariffForm.value);
  }

  //Deactivate swipe of tabgroup
  public select(event): void {
    if (event.isSwiped) {
      event.cancel = true;
    }
  }

  public getTariff(selectedTariffId: number): void {
    this.tariffService.getTariff(selectedTariffId).subscribe(data => {
      this.selectedTariff = data;
      this.selectedTariff.assignedZones.sort((a, b) => (a.name.localeCompare(b.name)));
      this.tabGroup?.select(0);
      this.updateForm();
    });
  }

  public deleteTariff() {
    const dialog = this.modalService.showOkCancelDialog('i18n.core.modal.delete.title', 'i18n.core.modal.delete.content');
    dialog.onOkSubject.subscribe(() => {
      this.tariffService.deleteParkingTariff(this.selectedTariff.id).subscribe(data => {
        this.tariffDeleted.emit(this.selectedTariff.id);
        this.closeForm();
      });
    });
  }

  public openAddTariff() {
    this.canDeactivate().subscribe(data => {
      if (data) {
        this.selectedTariff = null;
        this.resetForm();
        this.isAddTariff = true;
      }
    });
  }

  public onCancelClicked(): void {
    this.canDeactivate().subscribe(data => {
      if (data) {
        this.closeForm();
        this.cancel.emit();
      }
    });
  }

  public closeForm() {
    this.selectedTariff = null;
    this.isAddTariff = false;
    this.resetForm();
  }

  public submitForm() {
    if (this.selectedTariff) {
      this.tariffService.updateParkingTariff(this.getUpdateDTOFromFormData()).subscribe(data => {
        this.tariffUpdated.emit(data);
        //this.closeForm();
        this.selectedTariff = data;
        this.updateForm();
      });
    }
    if (this.isAddTariff) {
      this.tariffService.createParkingTariff(this.getCreateDTOFromFormData()).subscribe(data => {
        this.tariffCreated.emit(data);
        this.closeForm();
      });
    }
  }

  public canDeactivate(): Observable<boolean> {
    if (!(JSON.stringify(this.formInitialValue) === JSON.stringify(this.tariffForm.value))) {
      const dialog = this.modalService.showOkCancelDialog('i18n.errors.discard-dialog.title', 'i18n.errors.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(mapTo(true)),
        dialog.onCancelSubject.pipe(mapTo(false)),
      );
    }
    return of(true);
  }

  private resetForm(): void {
    this.tariffForm.enable();
    this.tariffForm.reset({
      general: ({
        id: null,
        name: '',
        active: true,
        description: '',
        modifiedBy: null,
        modifiedOn: null,
        placeOfResidence: PlaceOfResidence.LocalCommunity,
        isTwentyFourSevenTariff: false,
        isDeviceControlTariff: false,
        timeWindows: ({
          weekdays: ({
            allowedMonday: false,
            allowedTuesday: false,
            allowedWednesday: false,
            allowedThursday: false,
            allowedFriday: false,
            allowedSaturday: false,
            allowedSunday: false,
          }),
          startTime: null,
          durationForm: ({
            days: null,
            hours: null,
            minutes: null
          })
        }),
        maxParkingDurationInMinForm: ({
          days: null,
          hours: null,
          minutes: null
        }),
      }),
      prices: ({
        tariffCurrency: Currency.CHF,
        priceType: TariffPriceType.LinearPrice,
        pricePerHour: null,
        maximumPrice: null,
        fixedPrice: null,
        tieredPrices: [],
        linearPriceMinStepInMinutes: 1,
      }),
    });
    this.formInitialValue = JSON.parse(JSON.stringify(this.tariffForm.value));
    this.tariffForm.updateValueAndValidity();
    this.tariffForm.markAsPristine();
    this.updateFormChangedState(false);
  }

  private updateForm(): void {
    if (this.selectedTariff) {
      this.tariffForm.enable();
      this.tariffForm.reset({
        general: {
          id: this.selectedTariff.id,
          name: this.selectedTariff.name ?? '',
          modifiedBy: this.selectedTariff.modifiedBy ?? null,
          modifiedOn: this.selectedTariff.modifiedOn ? this.selectedTariff.modifiedOn.toFormat('dd.MM.yyyy HH:mm:ss') : null,
          active: this.selectedTariff.active,
          description: this.selectedTariff.description ?? '',
          placeOfResidence: this.selectedTariff.placeOfResidence,
          isTwentyFourSevenTariff: this.selectedTariff.isTwentyFourSevenTariff,
          isDeviceControlTariff: this.selectedTariff.isDeviceControlTariff,
          timeWindows: {
            weekdays: {
              allowedMonday: BitHelper.isBitSet(this.selectedTariff.onWeekDays, 0),
              allowedTuesday: BitHelper.isBitSet(this.selectedTariff.onWeekDays, 1),
              allowedWednesday: BitHelper.isBitSet(this.selectedTariff.onWeekDays, 2),
              allowedThursday: BitHelper.isBitSet(this.selectedTariff.onWeekDays, 3),
              allowedFriday: BitHelper.isBitSet(this.selectedTariff.onWeekDays, 4),
              allowedSaturday: BitHelper.isBitSet(this.selectedTariff.onWeekDays, 5),
              allowedSunday: BitHelper.isBitSet(this.selectedTariff.onWeekDays, 6),
            },
            startTime: this.selectedTariff.startTime ? DateHelper.setDateTimeTo1970FromDuration(this.selectedTariff.startTime).toJSDate() : null,
            durationForm: MultiFieldDurationPickerHelper.createDurationMembers(this.selectedTariff.durationInMinutes)
          },
          maxParkingDurationInMinForm: MultiFieldDurationPickerHelper.createDurationMembers(this.selectedTariff.maxParkingDurationInMin),
        },
        prices: {
          priceType: this.selectedTariff.priceType ?? TariffPriceType.LinearPrice,
          tariffCurrency: this.selectedTariff.tariffCurrency,
          pricePerHour: this.selectedTariff.pricePerHour,
          maximumPrice: this.selectedTariff.maximumPrice,
          fixedPrice: this.selectedTariff.fixedPrice,
          tieredPrices: !!this.selectedTariff.tieredPrices?.length ? this.selectedTariff.tieredPrices : [],
          linearPriceMinStepInMinutes: this.selectedTariff.linearPriceMinStepInMinutes,
        },
        blacklist: this.selectedTariff.blacklistedLicensePlates ?? []
      });

      if (this.selectedTariff.isTwentyFourSevenTariff) {
        this.tariffForm.controls.general.controls.timeWindows.disable();
      } else {
        this.tariffForm.controls.general.controls.timeWindows.enable();
      }

      if (this.selectedTariff.hasActiveSubscription) {
        this.tariffForm.disable();
        this.tariffForm.controls.general.controls.active.enable();
        this.tariffForm.controls.general.controls.placeOfResidence.enable();
      }
      this.tariffForm.controls.general.controls.name.enable();
      this.tariffForm.controls.general.controls.description.enable();
      this.formInitialValue = JSON.parse(JSON.stringify(this.tariffForm.value));
      this.updateFormChangedState(false);
      this.tariffForm.updateValueAndValidity();
      this.tariffForm.markAsPristine();
    } else {
      this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message');
    }
    this.isBlacklistChanged = false;
  }

  private getUpdateDTOFromFormData(): UpdateParkingTariffDTO {
    const rawValue = this.tariffForm.getRawValue();
    const startTime = rawValue.general.isTwentyFourSevenTariff ? null : DateHelper.getTimeOfDayDurationFromJsDate(rawValue.general.timeWindows.startTime);
    const dto = new UpdateParkingTariffDTO({
      id: this.selectedTariff.id,
      name: rawValue.general.name,
      active: rawValue.general.active,
      onWeekDays: rawValue.general.isTwentyFourSevenTariff ? null : WeekDayFlagsHelper.daysToFlag(rawValue.general.timeWindows.weekdays),
      description: rawValue.general.description,
      placeOfResidence: rawValue.general.placeOfResidence,
      isTwentyFourSevenTariff: rawValue.general.isTwentyFourSevenTariff,
      isDeviceControlTariff: rawValue.general.isDeviceControlTariff,
      startTime,
      durationInMinutes: rawValue.general.isTwentyFourSevenTariff ? null :
        MultiFieldDurationPickerHelper.calculateTotalMinutes(rawValue.general.timeWindows.durationForm.days, rawValue.general.timeWindows.durationForm.hours,
          rawValue.general.timeWindows.durationForm.minutes, false),
      maxParkingDurationInMin: MultiFieldDurationPickerHelper.calculateTotalMinutes(rawValue.general.maxParkingDurationInMinForm.days,
        rawValue.general.maxParkingDurationInMinForm.hours,
        rawValue.general.maxParkingDurationInMinForm.minutes, true),
      priceType: rawValue.prices.priceType,
      tariffCurrency: rawValue.prices.tariffCurrency,
      pricePerHour: rawValue.prices.pricePerHour,
      maximumPrice: rawValue.prices.maximumPrice,
      fixedPrice: rawValue.prices.fixedPrice,
      tieredPrices: rawValue.prices.tieredPrices?.map(tp => {
        tp.id = tp.id < 0 ? 0 : tp.id;
        return new ParkingTariffTieredPriceDTO(tp);
      }),
      linearPriceMinStepInMinutes: rawValue.prices.linearPriceMinStepInMinutes,
      blacklistedLicensePlates: rawValue.blacklist.map(lp => new BlacklistLicensePlateDTO(lp)),
    });
    return dto;
  }

  private getCreateDTOFromFormData(): CreateParkingTariffDTO {
    const rawValue = this.tariffForm.getRawValue();
    const zoneIds = this._detailInitInfo.parentType === AdministrationItemType.Zone ? [this._detailInitInfo.parentId] : null;
    const subscriptionId = this._detailInitInfo.parentType === AdministrationItemType.Subscription ? this._detailInitInfo.parentId : null;
    const startTime = rawValue.general.isTwentyFourSevenTariff ? null : DateHelper.getTimeOfDayDurationFromJsDate(rawValue.general.timeWindows.startTime);
    const dto = new CreateParkingTariffDTO({
      clientId: this._detailInitInfo.clientId,
      name: rawValue.general.name,
      active: rawValue.general.active,
      onWeekDays: rawValue.general.isTwentyFourSevenTariff ? null : WeekDayFlagsHelper.daysToFlag(rawValue.general.timeWindows.weekdays),
      description: rawValue.general.description,
      placeOfResidence: rawValue.general.placeOfResidence,
      isTwentyFourSevenTariff: rawValue.general.isTwentyFourSevenTariff,
      isDeviceControlTariff: rawValue.general.isDeviceControlTariff,
      startTime,
      durationInMinutes: rawValue.general.isTwentyFourSevenTariff ? null :
        MultiFieldDurationPickerHelper.calculateTotalMinutes(rawValue.general.timeWindows.durationForm.days, rawValue.general.timeWindows.durationForm.hours,
          rawValue.general.timeWindows.durationForm.minutes, false),
      maxParkingDurationInMin: MultiFieldDurationPickerHelper.calculateTotalMinutes(rawValue.general.maxParkingDurationInMinForm.days,
        rawValue.general.maxParkingDurationInMinForm.hours,
        rawValue.general.maxParkingDurationInMinForm.minutes, true),
      priceType: rawValue.prices.priceType,
      tariffCurrency: rawValue.prices.tariffCurrency,
      pricePerHour: rawValue.prices.pricePerHour,
      maximumPrice: rawValue.prices.maximumPrice,
      fixedPrice: rawValue.prices.fixedPrice,
      tieredPrices: rawValue.prices.tieredPrices?.map(tp => {
        tp.id = tp.id < 0 ? 0 : tp.id;
        return new ParkingTariffTieredPriceDTO(tp);
      }),
      assignedZoneIds: zoneIds,
      linearPriceMinStepInMinutes: rawValue.prices.linearPriceMinStepInMinutes,
      subscriptionId,
    });
    return dto;
  }

  private updateFormChangedState(hasChanges: boolean) {
    if (this.formChanged !== hasChanges) {
      this.formChanged = hasChanges;
      this.hasPendingDetailChangesChanged.emit(hasChanges);
    }
  }

  public onBlacklistLicensePlatesChange(blackList: BlacklistLicensePlateDTO[]): void {
    this.isBlacklistChanged = true;
    this.selectedTariff.blacklistedLicensePlates = blackList;
    this.tariffForm.controls.blacklist.setValue(blackList);
    this.tariffForm.controls.blacklist.markAsDirty();
    this.tariffForm.controls.blacklist.updateValueAndValidity();
  }
}

